import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import {
  Calendar, Loader2, Mail, Download, Check,
  UserRound, Settings as SettingsIcon, Receipt, AlertTriangle,
} from "lucide-react";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger,
  DialogFooter, DialogDescription,
} from "../ui/dialog";
import { Button } from "../ui/button";
import { toast } from "sonner";
import api from "../../lib/api";

/** Return the quarter (1-4) from a 0-indexed month. */
const monthToQuarter = (m) => Math.floor(m / 3) + 1;

/**
 * Default to the most recently-closed quarter:
 * e.g. in Feb (month 1) → Q4 of last year; in Apr (month 3) → Q1 of current year.
 */
function defaultQuarter() {
  const d = new Date();
  const y = d.getFullYear();
  const m = d.getMonth(); // 0-11
  const currentQ = monthToQuarter(m);
  if (currentQ === 1) return { year: y - 1, quarter: 4 };
  return { year: y, quarter: currentQ - 1 };
}

/**
 * Filing window — in Spain the Modelo 303 is due in the first 20 days of
 * the month following each quarter (30-Jan for Q4 annual extension).
 * Returns { active: bool, daysLeft: int, deadlineIso, urgent: bool }.
 */
function filingWindow(year, quarter) {
  const today = new Date();
  // Filing month = first month after quarter end
  const filingMonth = quarter === 4 ? 0 : quarter * 3;           // 0-11
  const filingYear = quarter === 4 ? year + 1 : year;
  const deadlineDay = quarter === 4 ? 30 : 20;
  const startOfMonth = new Date(filingYear, filingMonth, 1, 0, 0, 0);
  const deadline = new Date(filingYear, filingMonth, deadlineDay, 23, 59, 59);
  const active = today >= startOfMonth && today <= deadline;
  const daysLeft = Math.ceil((deadline.getTime() - today.getTime()) / 86400000);
  return {
    active,
    daysLeft,
    deadlineIso: deadline.toISOString().slice(0, 10),
    urgent: active && daysLeft <= 5,
  };
}

export default function ExportQuarterForAccountantDialog({ restaurantId }) {
  const [open, setOpen] = useState(false);
  const initial = useMemo(defaultQuarter, []);
  const [year, setYear] = useState(initial.year);
  const [quarter, setQuarter] = useState(initial.quarter);
  const [savedEmail, setSavedEmail] = useState("");
  const [emailLoading, setEmailLoading] = useState(false);
  const [busy, setBusy] = useState(null);

  useEffect(() => {
    if (!open || !restaurantId) return;
    setEmailLoading(true);
    api.getAccountantEmail(restaurantId)
      .then(r => setSavedEmail(r.accountant_email || ""))
      .catch(() => {})
      .finally(() => setEmailLoading(false));
  }, [open, restaurantId]);

  const yearOptions = Array.from({ length: 4 }, (_, i) => new Date().getFullYear() - i);

  const window = useMemo(() => filingWindow(initial.year, initial.quarter), [initial]);
  const winSelected = useMemo(() => filingWindow(year, quarter), [year, quarter]);

  const onDownload = async () => {
    setBusy("download");
    try {
      const { blob, filename } = await api.downloadFinanceQuarterExport(restaurantId, year, quarter);
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      toast.success(`ZIP trimestre ${year}-Q${quarter} descargado`);
    } catch (e) {
      toast.error(e.message || "Error descargando export");
    } finally {
      setBusy(null);
    }
  };

  const onSendEmail = async () => {
    if (!savedEmail) {
      toast.error("Configura primero el email del contable en Ajustes");
      return;
    }
    setBusy("email");
    try {
      const r = await api.sendFinanceQuarterExportEmail(restaurantId, year, quarter, savedEmail);
      toast.success(`${r.year}-Q${r.quarter} enviado a ${r.sent_to}`);
      setOpen(false);
    } catch (e) {
      toast.error(e.message || "Error enviando email");
    } finally {
      setBusy(null);
    }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <div className="relative inline-flex">
          <Button
            size="sm"
            variant={window.active ? "default" : "outline"}
            className={window.active ? (window.urgent ? "bg-red-600 hover:bg-red-700 text-white" : "bg-primary text-primary-foreground") : ""}
            data-testid="export-quarter-accountant-btn"
          >
            <Receipt className="h-3.5 w-3.5 mr-1.5" />
            IVA trimestral
          </Button>
          {window.active && (
            <span
              className="absolute -top-1.5 -right-1.5 flex h-2.5 w-2.5"
              aria-label={`Modelo 303 ${initial.year}-Q${initial.quarter}`}
              data-testid="quarter-filing-pulse"
            >
              <span className={`animate-ping absolute inline-flex h-full w-full rounded-full opacity-75 ${window.urgent ? "bg-red-400" : "bg-amber-400"}`} />
              <span className={`relative inline-flex rounded-full h-2.5 w-2.5 ${window.urgent ? "bg-red-500" : "bg-amber-500"}`} />
            </span>
          )}
        </div>
      </DialogTrigger>
      <DialogContent className="sm:max-w-md" data-testid="export-quarter-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Receipt className="h-5 w-5 text-primary" />
            IVA trimestral — Modelo 303
          </DialogTitle>
          <DialogDescription>
            Genera un ZIP con facturas del trimestre, gastos, ventas diarias y
            un resumen IVA ready-to-paste en el Modelo 303.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-2">
          {winSelected.active && (
            <div
              className={`rounded-lg border p-3 flex items-start gap-2 ${
                winSelected.urgent
                  ? "border-red-200 bg-red-50"
                  : "border-amber-200 bg-amber-50"
              }`}
              data-testid="quarter-filing-banner"
            >
              <AlertTriangle className={`h-4 w-4 mt-0.5 shrink-0 ${winSelected.urgent ? "text-red-700" : "text-amber-700"}`} />
              <div className="flex-1">
                <p className={`text-sm font-semibold ${winSelected.urgent ? "text-red-900" : "text-amber-900"}`}>
                  Modelo 303 {year}-Q{quarter} · vence el {winSelected.deadlineIso}
                </p>
                <p className={`text-[11px] mt-0.5 ${winSelected.urgent ? "text-red-800/80" : "text-amber-800/80"}`}>
                  {winSelected.daysLeft > 0
                    ? <>Te quedan <b>{winSelected.daysLeft} día{winSelected.daysLeft === 1 ? "" : "s"}</b> para presentar.</>
                    : <>Plazo vencido. Envíalo hoy mismo al asesor.</>}
                </p>
              </div>
            </div>
          )}

          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <label htmlFor="q-year" className="text-xs font-medium">Año</label>
              <select
                id="q-year"
                value={year}
                onChange={(e) => setYear(parseInt(e.target.value, 10))}
                className="flex h-9 w-full rounded-md border border-input bg-transparent px-3 py-1 text-sm shadow-sm focus:outline-none focus:ring-2 focus:ring-ring"
                data-testid="quarter-year-select"
              >
                {yearOptions.map(y => <option key={y} value={y}>{y}</option>)}
              </select>
            </div>
            <div className="space-y-1.5">
              <label htmlFor="q-quarter" className="text-xs font-medium">Trimestre</label>
              <select
                id="q-quarter"
                value={quarter}
                onChange={(e) => setQuarter(parseInt(e.target.value, 10))}
                className="flex h-9 w-full rounded-md border border-input bg-transparent px-3 py-1 text-sm shadow-sm focus:outline-none focus:ring-2 focus:ring-ring"
                data-testid="quarter-q-select"
              >
                <option value={1}>Q1 (Ene–Mar)</option>
                <option value={2}>Q2 (Abr–Jun)</option>
                <option value={3}>Q3 (Jul–Sep)</option>
                <option value={4}>Q4 (Oct–Dic)</option>
              </select>
            </div>
          </div>

          {/* Email read-only */}
          <div
            className="rounded-lg border border-border bg-muted/40 p-3 flex items-center gap-3"
            data-testid="quarter-email-readonly"
          >
            <UserRound className="h-4 w-4 text-muted-foreground shrink-0" />
            <div className="flex-1 min-w-0">
              <div className="text-[11px] text-muted-foreground uppercase tracking-wide font-semibold">
                Contable
              </div>
              {emailLoading ? (
                <div className="flex items-center gap-1.5 mt-0.5">
                  <Loader2 className="h-3.5 w-3.5 animate-spin text-muted-foreground" />
                  <span className="text-xs text-muted-foreground">Cargando…</span>
                </div>
              ) : savedEmail ? (
                <div className="text-sm font-medium truncate flex items-center gap-1.5">
                  <Mail className="h-3.5 w-3.5 text-emerald-700" />
                  <span className="truncate">{savedEmail}</span>
                  <Check className="h-3 w-3 text-emerald-700 shrink-0" />
                </div>
              ) : (
                <div className="text-xs text-amber-700">Sin email configurado</div>
              )}
            </div>
            <Button
              asChild size="sm" variant="ghost" className="shrink-0"
              data-testid="quarter-email-edit-link"
            >
              <Link to="/app/settings?tab=profile">
                <SettingsIcon className="h-3.5 w-3.5 mr-1" />
                {savedEmail ? "Editar" : "Configurar"}
              </Link>
            </Button>
          </div>

          <div className="rounded-lg bg-muted/50 border border-border p-3 text-xs text-foreground space-y-1">
            <p className="font-semibold flex items-center gap-1.5">
              <Calendar className="h-3.5 w-3.5" /> El ZIP incluye:
            </p>
            <ul className="list-disc list-inside space-y-0.5 text-muted-foreground">
              <li><code className="text-[10px] bg-card px-1 rounded">facturas/YYYY-MM/</code> agrupadas por mes</li>
              <li><code className="text-[10px] bg-card px-1 rounded">gastos.csv</code> del trimestre</li>
              <li><code className="text-[10px] bg-card px-1 rounded">ventas.csv</code> diarias por canal</li>
              <li><code className="text-[10px] bg-card px-1 rounded">resumen_trimestral.csv</code> 3 meses + TOTAL</li>
              <li><code className="text-[10px] bg-card px-1 rounded">iva_303.csv</code> + <code className="text-[10px] bg-card px-1 rounded">modelo_303.pdf</code> con base/cuota/repercutido</li>
            </ul>
            <p className="text-[10px] text-muted-foreground mt-1.5">
              Importes orientativos — tu asesor debe validarlos antes de presentar.
            </p>
          </div>
        </div>

        <DialogFooter className="gap-2 sm:gap-2">
          <Button
            variant="outline"
            onClick={onDownload}
            disabled={!!busy}
            data-testid="export-quarter-download-btn"
          >
            {busy === "download"
              ? <Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" />
              : <Download className="h-3.5 w-3.5 mr-1.5" />}
            Descargar ZIP
          </Button>
          <Button
            onClick={onSendEmail}
            disabled={!!busy || !savedEmail}
            data-testid="export-quarter-send-email-btn"
          >
            {busy === "email"
              ? <Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" />
              : <Mail className="h-3.5 w-3.5 mr-1.5" />}
            Enviar al contable
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
